import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, Pause, Lock, Share2, ListPlus } from "lucide-react";
import { getTrackPage, getRelease } from "../lib/api";
import { Reveal } from "../components/Motion";
import { usePlayer, PLAYABLE } from "../context/PlayerContext";
import { setMeta } from "../lib/seo";
import { track as trackEvent } from "../lib/analytics";
import { liveStreamingLinks } from "../lib/streaming";

const DIM_ROUTES = { "01": "/dimension-i", "02": "/dimension-ii", "03": "/dimension-iii", "04": "/dimension-iv" };

export default function TrackPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { playTrack, addToQueue, isCurrent, playing, toggle } = usePlayer();
  const relRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setT(null);
    getTrackPage(slug)
      .then((d) => {
        setT(d);
        setMeta({
          title: d.social?.title || `${d.title} — ${d.release.title} | Motion & Meaning`,
          description: d.social?.description || d.editorial?.meaning || `Listen to "${d.title}" by Motion & Meaning — from ${d.release.title}.`,
          image: d.social?.image || d.artwork || d.release.artwork,
          path: `/track/${slug}`,
        });
        trackEvent("track_view", { title: d.title });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6" data-testid="track-notfound">
        <p className="font-serif text-4xl">This signal does not exist.</p>
        <Link to="/music" className="font-mono text-[11px] tracking-[0.2em] text-gold">← BACK TO MUSIC</Link>
      </div>
    );
  if (!t) return <div className="min-h-screen" />;

  const playable = PLAYABLE.includes(t.availability);
  const cur = isCurrent(t.release.slug, t.trackNumber);
  const artwork = t.artwork || t.release.artwork;
  const ed = t.editorial || {};
  const playCtx = { releaseSlug: t.release.slug, releaseTitle: t.release.title, artwork, dimension: t.release.dimension };

  const onPlay = async () => {
    if (cur) { toggle(); return; }
    let queueTracks = relRef.current?.trackList;
    if (!queueTracks) {
      try { const r = await getRelease(t.release.slug); relRef.current = r; queueTracks = r.trackList; } catch {}
    }
    playTrack(t, { ...playCtx, queue: queueTracks });
  };

  const onQueue = () => { if (addToQueue(t, playCtx)) toast.success("Added to queue."); };

  const share = async () => {
    const url = `${window.location.origin}/api/share/track/${slug}`;
    if (navigator.share) {
      try { await navigator.share({ title: `${t.title} — Motion & Meaning`, url }); return; } catch {}
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — it unfurls with this track's artwork.");
    } catch { toast.error("Could not copy link."); }
  };

  return (
    <div data-testid="track-page" className="pt-28 md:pt-36 pb-32 min-h-screen">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20">
          {/* Artwork */}
          <div className="md:col-span-5">
            <Reveal y={50}>
              <div className="md:sticky md:top-32">
                <div className="relative overflow-hidden rounded-[18px]">
                  <img src={artwork} alt={`${t.title} artwork`} className="w-full aspect-square object-cover" data-testid="track-artwork" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-[18px]" />
                </div>
                <div className="flex items-center gap-4 mt-6">
                  {playable ? (
                    <button
                      onClick={onPlay}
                      data-testid="track-play-btn"
                      aria-label={cur && playing ? `Pause ${t.title}` : `Play ${t.title}`}
                      className="flex items-center gap-3 bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:bg-white transition-colors"
                    >
                      {cur && playing ? <Pause size={14} /> : <Play size={14} />} {cur && playing ? "PAUSE" : t.availability === "TEASER" ? "PLAY TEASER" : "LISTEN"}
                    </button>
                  ) : (
                    <span className="flex items-center gap-3 border border-white/15 text-white/40 font-mono text-[10px] tracking-[0.25em] px-8 py-4" data-testid="track-locked-chip">
                      <Lock size={13} /> {t.lockLabel || "SIGNAL NOT YET AVAILABLE"}
                    </span>
                  )}
                  {playable && (
                    <button onClick={onQueue} data-testid="track-queue-btn" aria-label={`Add ${t.title} to queue`}
                      className="w-12 h-12 border border-white/20 text-white/60 flex items-center justify-center hover:border-gold hover:text-gold transition-colors">
                      <ListPlus size={16} />
                    </button>
                  )}
                  <button onClick={share} data-testid="track-share-btn" aria-label="Share this track"
                    className="w-12 h-12 border border-white/20 text-white/60 flex items-center justify-center hover:border-gold hover:text-gold transition-colors">
                    <Share2 size={16} />
                  </button>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Content */}
          <div className="md:col-span-7">
            <Reveal>
              <Link to={`/music/${t.release.slug}`} data-testid="track-release-link"
                className="font-mono text-[10px] tracking-[0.35em] text-white/45 hover:text-gold transition-colors">
                {t.release.title?.toUpperCase()} · {t.release.releaseType?.toUpperCase()}
              </Link>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95] text-white mt-5" data-testid="track-title">
                {t.title}
              </h1>
              <p className="font-mono text-[10px] tracking-[0.3em] text-white/35 mt-5">
                TRACK {String(t.trackNumber).padStart(2, "0")}{t.duration ? ` · ${t.duration}` : ""}
                {t.release.dimension ? ` · DIMENSION ${t.release.dimension}` : ""}
              </p>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="mt-14" data-testid="track-lyrics">
                <p className="font-mono text-[10px] tracking-[0.3em] text-gold/80 mb-5">LYRICS</p>
                {ed.lyrics ? (
                  <p className="font-serif text-xl md:text-2xl font-light leading-relaxed text-white/85 whitespace-pre-line">{ed.lyrics}</p>
                ) : (
                  <p className="font-mono text-[10px] tracking-[0.25em] text-white/25">NOT YET PUBLISHED.</p>
                )}
              </div>
              <div className="mt-12" data-testid="track-meaning">
                <p className="font-mono text-[10px] tracking-[0.3em] text-gold/80 mb-5">BEHIND THE MEANING</p>
                {ed.meaning ? (
                  <p className="text-white/60 leading-relaxed max-w-xl">{ed.meaning}</p>
                ) : (
                  <p className="font-mono text-[10px] tracking-[0.25em] text-white/25">NOT YET PUBLISHED.</p>
                )}
              </div>
            </Reveal>

            {(t.credits || (t.release.credits || []).length > 0) && (
              <Reveal delay={0.1}>
                <div className="mt-14 border border-white/10 p-8" data-testid="track-credits">
                  <p className="font-mono text-[10px] tracking-[0.25em] text-white/40 mb-4">CREDITS</p>
                  {t.credits ? <p className="text-white/70 text-sm">{t.credits}</p> : null}
                  {(t.release.credits || []).map((c, i) => (
                    <div key={i} className="flex justify-between text-sm py-1">
                      <span className="text-white/40">{c.role}</span>
                      <span className="text-white/80">{c.name}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}

            {liveStreamingLinks(t.release.streamingLinks).length > 0 && (
              <Reveal delay={0.11}>
                <div className="mt-10" data-testid="track-streaming-links">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-white/40 mb-4">LISTEN ON</p>
                  <div className="flex flex-wrap gap-3">
                    {liveStreamingLinks(t.release.streamingLinks).map((s, i) => (
                      <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                        data-testid={`track-stream-${(s.platform || "link").toLowerCase().replace(/\s+/g, "-")}`}
                        className="border border-white/15 px-5 py-3 font-mono text-[10px] tracking-[0.2em] text-white/70 hover:border-gold hover:text-gold transition-colors">
                        {(s.label || s.platform).toUpperCase()} →
                      </a>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}

            {t.related?.length > 0 && (
              <Reveal delay={0.12}>
                <div className="mt-14" data-testid="track-related">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-white/40 mb-4">FROM THE SAME TRANSMISSION</p>
                  {t.related.slice(0, 5).map((r) => (
                    <button key={r.slug || r.trackNumber} onClick={() => r.slug && navigate(`/track/${r.slug}`)}
                      data-testid={`track-related-${r.trackNumber}`}
                      className="group w-full flex items-center justify-between py-4 border-t border-white/10 text-left">
                      <span className="font-serif text-xl text-white/80 group-hover:text-gold transition-colors">
                        <span className="font-mono text-[10px] text-gold/70 mr-4">{String(r.trackNumber).padStart(2, "0")}</span>
                        {r.title}
                      </span>
                      <span className="font-mono text-[10px] text-white/35">{r.duration}</span>
                    </button>
                  ))}
                </div>
              </Reveal>
            )}

            <Reveal delay={0.14}>
              <div className="flex flex-wrap gap-5 mt-14">
                <button onClick={() => navigate(`/music/${t.release.slug}`)} data-testid="track-cta-album"
                  className="bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:bg-white transition-colors">EXPLORE THE ALBUM</button>
                {t.release.dimension && DIM_ROUTES[t.release.dimension] && (
                  <button onClick={() => navigate(DIM_ROUTES[t.release.dimension])} data-testid="track-cta-dimension"
                    className="border border-white/25 text-white/80 font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:border-gold hover:text-gold transition-colors">
                    ENTER DIMENSION {t.release.dimension}
                  </button>
                )}
                <button onClick={() => navigate("/store")} data-testid="track-cta-store"
                  className="font-mono text-[11px] tracking-[0.25em] text-white/50 hover:text-gold transition-colors self-center">OWN THE MUSIC →</button>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
